import { useDebounce } from '@utils/useDebounce';
import { useCallback, useEffect, useRef } from 'react';
import { ProjectFormPresence, useFormFillerPresenceQuery, useRemoveFormPresenceMutation, useUpdateFormPresenceMutation } from '../../../../gql';
import { useFormPresenceUpdatedSubscription } from '../../../../gql/subscriptions';
import { uniqueClientId } from '../../../application/apollo-client';

type Presence = Pick<ProjectFormPresence, 'userId' | 'displayName' | 'fieldId' | 'matrixColumnId' | 'matrixRowId'>;

export const useFormFillerPresence = (submissionId: number) => {

  const { mutate: removePresence } = useRemoveFormPresenceMutation({ onError: () => {/* prevent default error handling */ } });
  const { mutate: updatePresence } = useUpdateFormPresenceMutation({ onError: () => {/* prevent default error handling */ } });

  const updatePresenceDebounced = useDebounce(updatePresence, 1000);

  const presences = useRef<Presence[]>([]);

  const { data: presencesData } = useFormFillerPresenceQuery({ formSubmissionId: submissionId }, {
    staleTime: 1000 * 60 * 5
  });

  useFormPresenceUpdatedSubscription({
    variables: { submissionId },
    onData: dataEvent => {
      presences.current = (dataEvent.data.data?.formPresenceUpdated ?? [])
        .filter(p => p.uniqueClientId != uniqueClientId);
    }
  });

  const updateFieldPresence = useCallback((fieldId?: number, matrixRowId?: number, matrixColumnId?: number) => {
    updatePresenceDebounced({
      input: { submissionId, uniqueClientId, fieldId, matrixRowId, matrixColumnId }
    });
  }, [submissionId, updatePresenceDebounced]);

  const removeFormPresence = useCallback(() => {
    removePresence({
      input: { uniqueClientId, submissionId }
    });
  }, [removePresence, submissionId]);


  const addFormPresence = useCallback(() => {
    updatePresence({
      input: { uniqueClientId, submissionId, fieldId: undefined }
    });
  }, [submissionId, updatePresence]);

  useEffect(() => {
    presences.current = (presencesData?.formPresences ?? [])
      .filter(p => p.uniqueClientId != uniqueClientId);
  }, [presencesData]);

  return { presences: presences.current ?? [], removeFormPresence, addFormPresence, updateFieldPresence };
};